import React, { useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useUi } from '../../context/ui.context';

interface DashboardHeaderProps {
  analyticsAvailable: boolean;
  greeting?: string;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({ analyticsAvailable, greeting }) => {
  const [t] = useTranslation();
  const { setSmallBar } = useUi();

  useEffect(() => {
    const viewport = window.matchMedia('(max-width: 640px)');
    const collapseSidebar = () => {
      if (viewport.matches) setSmallBar(true);
    };
    collapseSidebar();
    viewport.addEventListener('change', collapseSidebar);
    return () => viewport.removeEventListener('change', collapseSidebar);
  }, [setSmallBar]);

  return (
    <Header>
      <Heading>
        <Title>{t('DASHBOARD_TITLE')}</Title>
        {greeting && <Greeting>{greeting}</Greeting>}
      </Heading>
      <Navigation aria-label={t('DASHBOARD_VIEWS')} $single={!analyticsAvailable}>
        <ViewLink to="/home" end>
          <Icon aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="7" rx="2" />
              <rect x="3" y="14" width="18" height="7" rx="2" />
              <path d="M7 6.5h.01M7 17.5h.01M15 6.5h3M15 17.5h3" />
            </svg>
          </Icon>
          <Copy>
            <Label>{t('DASHBOARD_INFRASTRUCTURE')}</Label>
            <Description>{t('DASHBOARD_INFRASTRUCTURE_DESCRIPTION')}</Description>
          </Copy>
        </ViewLink>
        {analyticsAvailable && (
          <ViewLink to="/analytics/dashboard" end>
            <Icon aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 4v16h16M8 15v-4M13 15V8M18 15V5" />
              </svg>
            </Icon>
            <Copy>
              <Label>{t('DD_TITLE')}</Label>
              <Description>{t('DASHBOARD_BUSINESS_DESCRIPTION')}</Description>
            </Copy>
          </ViewLink>
        )}
      </Navigation>
    </Header>
  );
};

const Header = styled.header`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  min-width: 0;
`;

const Heading = styled.div`
  min-width: 0;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 26px;
  line-height: 1.25;
  font-weight: 600;
  letter-spacing: -0.5px;
  color: ${({ theme }) => theme.palette[900]};
`;

const Greeting = styled.p`
  margin: 6px 0 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette[650]};
`;

const Navigation = styled.nav<{ $single: boolean }>`
  display: grid;
  grid-template-columns: ${({ $single }) => $single ? 'minmax(0, 1fr)' : 'repeat(2, minmax(0, 1fr))'};
  width: ${({ $single }) => $single ? '350px' : '700px'};
  max-width: 100%;
  padding: 5px;
  gap: 5px;
  box-sizing: border-box;
  background: ${({ theme }) => theme.palette[200]};
  border: 1px solid ${({ theme }) => theme.palette[350]};
  border-radius: 14px;

  @media (max-width: 640px) {
    width: 100%;
  }
`;

const Icon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  color: ${({ theme }) => theme.palette[650]};
  background: ${({ theme }) => theme.palette[350]};

  svg {
    width: 22px;
    height: 22px;
  }
`;

const ViewLink = styled(NavLink)`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid transparent;
  border-radius: 10px;
  color: ${({ theme }) => theme.palette[800]};
  text-decoration: none;
  transition: background 150ms ease, border-color 150ms ease, box-shadow 150ms ease;

  &:hover {
    background: ${({ theme }) => theme.palette[100]};
  }

  &[aria-current='page'] {
    background: ${({ theme }) => theme.palette[10]};
    border-color: ${({ theme }) => theme.isDark ? theme.palette[400] : theme.primary[100]};
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
    color: ${({ theme }) => theme.isDark ? theme.primary[300] : theme.primary[700]};

    ${Icon} {
      color: ${({ theme }) => theme.isDark ? theme.primary[300] : theme.primary[500]};
      background: ${({ theme }) => theme.isDark ? 'rgba(15, 104, 233, 0.18)' : theme.primary[50]};
    }
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.primary[400]};
    outline-offset: 2px;
  }

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 10px;
    ${Icon} {
      width: 30px;
      height: 30px;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const Copy = styled.span`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

const Label = styled.span`
  font-size: 14px;
  line-height: 1.4;
  font-weight: 600;
  overflow-wrap: anywhere;
`;

const Description = styled.span`
  font-size: 12px;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette[650]};
`;

export default DashboardHeader;
